/**
 * 月間シフト表示設定の型定義 (v2: 配列ベース多段表示対応)
 */

// ============================================================================
// v2 型定義（新）
// ============================================================================

/**
 * 従業員セルの表示選択肢
 */
export type EmployeeCellAreaOption =
    | "none"
    | "employeeName"
    | "employmentType"
    | "employmentTypeCode"
    | "employeeCode"
    | "monthlyContractHours"
    | "monthlyHolidayCount"
    | "monthlyWorkSummary";

/**
 * シフトセルの表示選択肢
 */
export type ShiftCellAreaOption =
    | "none"
    | "shiftInfo"       // シフト情報（自動: 休日名 > パターン > 勤務時間）
    | "breakMinutes"    // 休憩分数（例: "60分"）
    | "supportStore"    // 応援先店舗名
    | "workHours"       // 実労働時間（例: "8.0h"）
    | "totalHours";     // 拘束時間（例: "9.0h"）

/**
 * 従業員セルの1行設定（1スロット）
 */
export interface EmployeeRowConfig {
    content: EmployeeCellAreaOption;
}

/**
 * シフトセルの1行設定
 */
export interface ShiftCellRowConfig {
    content: ShiftCellAreaOption;
}

/**
 * シフト希望の表示内容の選択肢
 */
export type ShiftPreferenceContentOption =
    | "patternCode"     // シフトパターン名
    | "workTime"        // 勤務時間
    | "holidayType";    // 休日種類

/**
 * シフト希望行の設定
 */
export interface ShiftPreferenceConfig {
    /** 希望行を表示するか */
    enabled: boolean;
    /** 表示内容 */
    content: ShiftPreferenceContentOption;
    /** shift_detail に対する配置 ("top" = 上, "bottom" = 下) */
    position: "top" | "bottom";
}

/**
 * 月間シフト表示設定 (v2)
 *
 * - employeeRows / shiftRows の行数は独立（1〜4）
 * - セル高さは Math.max(employeeRows.length, shiftRows.length) で決定
 * - 行数が少ない側は垂直中央揃えで表示
 */
export interface MonthlyShiftCellDisplaySettings {
    version: 2;
    employeeRows: EmployeeRowConfig[];
    shiftRows: ShiftCellRowConfig[];
    /** シフト希望行の設定（希望表示モード時のみ表示） */
    shiftPreference?: ShiftPreferenceConfig;
}

// ============================================================================
// 制約定数
// ============================================================================

/** 最小行数 */
export const MIN_ROWS = 1;
/** 最大行数（将来拡張時はここを変更） */
export const MAX_ROWS = 4;

// ============================================================================
// セル高さ
// ============================================================================

/** 行数ごとの通常行エリアの高さ(px) */
export const BASE_ROW_HEIGHTS: Record<number, number> = {
    1: 36,
    2: 56,  // 現行 h-14
    3: 72,
    4: 88,
};

/** シフト希望行の追加高さ(px)：区切り線 + テキスト + 上下余白 */
export const EXTRA_ROW_HEIGHT = 32;

/**
 * セル高さを算出
 * @param employeeRowCount 従業員セルの行数
 * @param shiftRowCount シフトセルの行数
 * @param hasExtraRow 将来用: shift_preference 行あり
 */
export function getCellHeight(
    employeeRowCount: number,
    shiftRowCount: number,
    hasExtraRow = false
): number {
    const effectiveRows = Math.max(employeeRowCount, shiftRowCount);
    const base = BASE_ROW_HEIGHTS[effectiveRows] ?? BASE_ROW_HEIGHTS[2];
    return hasExtraRow ? base + EXTRA_ROW_HEIGHT : base;
}

// ============================================================================
// 選択肢ラベル定義
// ============================================================================

/** 従業員セル選択肢のラベル */
export const EMPLOYEE_CELL_OPTIONS: { value: EmployeeCellAreaOption; label: string }[] = [
    { value: "none", label: "表示しない" },
    { value: "employeeName", label: "従業員名" },
    { value: "employeeCode", label: "従業員コード" },
    { value: "employmentType", label: "雇用形態名" },
    { value: "employmentTypeCode", label: "雇用形態コード" },
    { value: "monthlyContractHours", label: "月間契約時間上限 / 下限" },
    { value: "monthlyHolidayCount", label: "月間公休日数上限 / 下限" },
    { value: "monthlyWorkSummary", label: "勤務日数 / 休日日数 / 勤務時間" },
];

/** 従業員セル1行目用の選択肢（名前系のみ） */
export const EMPLOYEE_ROW1_OPTIONS: { value: EmployeeCellAreaOption; label: string }[] = [
    { value: "employeeName", label: "従業員名" },
];

/** シフトセル選択肢のラベル */
export const SHIFT_CELL_OPTIONS: { value: ShiftCellAreaOption; label: string }[] = [
    { value: "none", label: "表示しない" },
    { value: "shiftInfo", label: "シフト情報" },
    { value: "breakMinutes", label: "休憩分数" },
    { value: "supportStore", label: "応援先店舗名" },
    { value: "workHours", label: "実労働時間" },
    { value: "totalHours", label: "拘束時間" },
];

/** 2行目以降で使える選択肢（1行目専用のshiftInfoを除外） */
export const SECONDARY_SHIFT_OPTIONS = SHIFT_CELL_OPTIONS.filter(
    (o) => o.value !== "shiftInfo"
);

/** シフト希望行の選択肢のラベル */
export const SHIFT_PREFERENCE_OPTIONS: { value: ShiftPreferenceContentOption; label: string }[] = [
    { value: "patternCode", label: "シフトパターン名" },
    { value: "workTime", label: "勤務時間" },
    { value: "holidayType", label: "休日種類" },
];

// ============================================================================
// デフォルト設定
// ============================================================================

export const DEFAULT_MONTHLY_SHIFT_CELL_DISPLAY: MonthlyShiftCellDisplaySettings = {
    version: 2,
    employeeRows: [
        { content: "employeeName" },
        { content: "employmentType" },
        { content: "monthlyWorkSummary" },
    ],
    shiftRows: [
        { content: "shiftInfo" },
        { content: "none" },
    ],
    shiftPreference: {
        enabled: false,
        content: "patternCode",
        position: "bottom",
    },
};

// ============================================================================
// サンプルデータ（設定画面プレビュー用）
// ============================================================================

export interface SampleEmployeeData {
    employeeName: string;
    employmentType: string;
    employmentTypeCode?: string;
    employeeCode: string;
    monthlyContractHours: string;
    monthlyHolidayCount: string;
    monthlyWorkSummary: string;
}

export const SAMPLE_EMPLOYEE_DATA: SampleEmployeeData = {
    employeeName: "田中太郎",
    employmentType: "社員",
    employmentTypeCode: "FT",
    employeeCode: "EMP001",
    monthlyContractHours: "160h / 200h",
    monthlyHolidayCount: "8日 / 10日",
    monthlyWorkSummary: "21日 / 9日 / 176h",
};

export interface SampleShiftData {
    patternCode: string;
    startTime: string;
    endTime: string;
    breakMinutes: number;
    breakTime: string;
    supportStoreName: string;
    comment: string;
    workHours: string;
    totalHours: string;
    isHoliday: boolean;
    holidayName: string;
}

export const SAMPLE_SHIFT_DATA: SampleShiftData = {
    patternCode: "A",
    startTime: "09:00",
    endTime: "18:00",
    breakMinutes: 60,
    breakTime: "12:00-13:00",
    supportStoreName: "新宿店",
    comment: "研修あり",
    workHours: "8.0h",
    totalHours: "9.0h",
    isHoliday: false,
    holidayName: "",
};

export interface SampleShiftPreferenceData {
    patternCode: string;
    workTime: string;
    holidayType: string;
}

export const SAMPLE_SHIFT_PREFERENCE_DATA: SampleShiftPreferenceData = {
    patternCode: "希A",
    workTime: "09:00-18:00",
    holidayType: "有休",
};

/**
 * 従業員セル選択値からサンプルデータの表示テキストを取得
 */
export const getEmployeeDisplayText = (
    option: EmployeeCellAreaOption,
    data: SampleEmployeeData
): string => {
    switch (option) {
        case "employeeName":
            return data.employeeName;
        case "employmentType":
            return data.employmentType;
        case "employmentTypeCode":
            return data.employmentTypeCode ?? "";
        case "employeeCode":
            return data.employeeCode;
        case "monthlyContractHours":
            return data.monthlyContractHours;
        case "monthlyHolidayCount":
            return data.monthlyHolidayCount;
        case "monthlyWorkSummary":
            return data.monthlyWorkSummary;
        case "none":
            return "";
        default:
            return "";
    }
};

/**
 * シフトセル選択値からサンプルデータの表示テキストを取得
 */
export const getShiftDisplayText = (
    option: ShiftCellAreaOption,
    data: SampleShiftData
): string => {
    switch (option) {
        case "shiftInfo":
            // 自動判定: 休日名 > パターン > 勤務時間
            if (data.holidayName) return data.holidayName;
            if (data.patternCode) return data.patternCode;
            return `${data.startTime}-${data.endTime}`;
        case "breakMinutes":
            return `${data.breakMinutes}分`;
        case "supportStore":
            return data.supportStoreName;
        case "workHours":
            return data.workHours;
        case "totalHours":
            return data.totalHours;
        case "none":
            return "";
        default:
            return "";
    }
};

/**
 * シフト希望選択値からサンプルデータの表示テキストを取得
 */
export const getShiftPreferenceDisplayText = (
    option: ShiftPreferenceContentOption,
    data: SampleShiftPreferenceData
): string => {
    switch (option) {
        case "patternCode":
            return data.patternCode;
        case "workTime":
            return data.workTime;
        case "holidayType":
            return data.holidayType;
        default:
            return "";
    }
};

// ============================================================================
// v1 → v2 マイグレーション
// ============================================================================

/** v1 の設定型（後方互換用） */
interface MonthlyShiftCellDisplaySettingsV1 {
    area1: string;
    area2: string;
    area3: string;
}

/**
 * 保存された設定を v2 形式に変換する
 * v1 (area1/area2/area3) → v2 (employeeRows/shiftRows)
 */
export function migrateMonthlySettings(
    raw: Record<string, unknown>
): MonthlyShiftCellDisplaySettings {
    // v2 判定: version フィールドあり
    if ("version" in raw && raw.version === 2) {
        const settings = raw as unknown as MonthlyShiftCellDisplaySettings;
        // left/right 形式の行を content 形式に変換（過渡期データの救済）
        if (settings.employeeRows) {
            settings.employeeRows = settings.employeeRows.map((row) => {
                if (!("content" in row) && "left" in row) {
                    const legacy = row as unknown as Record<string, string>;
                    return { content: (legacy.left ?? "none") as EmployeeCellAreaOption };
                }
                return row;
            });
        }
        return settings;
    }
    // v1 判定: area1 フィールドあり
    if ("area1" in raw) {
        const v1 = raw as unknown as MonthlyShiftCellDisplaySettingsV1;
        const migrated: EmployeeRowConfig[] = [
            { content: v1.area1 as EmployeeCellAreaOption },
        ];
        if (v1.area2 && v1.area2 !== "none") migrated.push({ content: v1.area2 as EmployeeCellAreaOption });
        if (v1.area3 && v1.area3 !== "none") migrated.push({ content: v1.area3 as EmployeeCellAreaOption });
        return {
            version: 2,
            employeeRows: migrated,
            shiftRows: [
                { content: "shiftInfo" },
                { content: "none" },
            ],
        };
    }
    // 判定不能 → デフォルト
    return { ...DEFAULT_MONTHLY_SHIFT_CELL_DISPLAY };
}

// ============================================================================
// 旧型定義（@deprecated - 段階的移行用）
// ============================================================================

/**
 * @deprecated v2 の EmployeeCellAreaOption を使用してください
 */
export type Area1Option = "employeeName";

/**
 * @deprecated v2 の EmployeeCellAreaOption を使用してください
 */
export type Area2And3Option =
    | "none"
    | "employmentType"
    | "employeeCode"
    | "monthlyContractHours"
    | "monthlyHolidayCount"
    | "monthlyWorkSummary";

/**
 * @deprecated v2 の EMPLOYEE_ROW1_OPTIONS を使用してください
 */
export const AREA1_OPTIONS: { value: Area1Option; label: string }[] = [
    { value: "employeeName", label: "従業員名" },
];

/**
 * @deprecated v2 の EMPLOYEE_CELL_OPTIONS を使用してください
 */
export const AREA2_AND_3_OPTIONS: { value: Area2And3Option; label: string }[] = [
    { value: "none", label: "表示しない" },
    { value: "employeeCode", label: "従業員コード" },
    { value: "employmentType", label: "雇用形態名" },
    { value: "monthlyContractHours", label: "月間契約時間上限 / 下限" },
    { value: "monthlyHolidayCount", label: "月間公休日数上限 / 下限" },
    { value: "monthlyWorkSummary", label: "勤務日数 / 休日日数 / 勤務時間" },
];

/**
 * @deprecated v2 の getEmployeeDisplayText を使用してください
 */
export const getDisplayText = (
    option: Area1Option | Area2And3Option,
    data: SampleEmployeeData
): string => {
    return getEmployeeDisplayText(option as EmployeeCellAreaOption, data);
};
